import React, { useState, useRef, useEffect } from 'react'
import { 
    Persona, 
    updatePersonaDetails, 
    updatePersonaProfilePic, 
    PersonaUpdate, 
    refetchPersonaImages,
    getPersonaCandidateImages,
    selectPersonaCandidateImage,
    CandidateImage,
    PersonaType
} from '../api/client'
import { Camera, RefreshCw } from 'lucide-react'

interface PersonaCardProps {
    persona: Persona
    actionButtons?: React.ReactNode
    editActionButtons?: React.ReactNode
    isCompact?: boolean
    onPersonaUpdated?: (updatedPersona: Persona) => void
    isAdmin?: boolean
    projectCategoryIds?: number[]
    isInSquad?: boolean
    isInProject?: boolean
}

const PersonaCard: React.FC<PersonaCardProps> = ({
    persona,
    editActionButtons,
    actionButtons,
    isCompact = false,
    onPersonaUpdated,
    isAdmin = false,
    projectCategoryIds = [],
    isInSquad = false,
    isInProject = false
}) => {
    const [isEditing, setIsEditing] = useState(false)
    const [firstName, setFirstName] = useState(persona.first_name)
    const [lastName, setLastName] = useState(persona.last_name || '')
    const [bio, setBio] = useState(persona.bio)
    const [interactionStyle, setInteractionStyle] = useState(persona.interaction_style)
    const [personaNickname, setPersonaNickname] = useState(persona.nickname || '')
    const [isSaving, setIsSaving] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isFetchingImage, setIsFetchingImage] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fetchMessage, setFetchMessage] = useState<string | null>(null)
    const [isBioExpanded, setIsBioExpanded] = useState(false)
    const [isStyleExpanded, setIsStyleExpanded] = useState(false)
    const [isBioTruncated, setIsBioTruncated] = useState(false)
    const [isStyleTruncated, setIsStyleTruncated] = useState(false)
    const [candidateImages, setCandidateImages] = useState<CandidateImage[]>([])
    const [viewedImageIds, setViewedImageIds] = useState<Set<number>>(new Set())
    const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const bioRef = useRef<HTMLParagraphElement>(null)
    const styleRef = useRef<HTMLParagraphElement>(null)

    const isUserOwned = persona.owner_id !== null
    
    
    // Display logic for names and archetype
    const hasFirstName = persona.first_name?.trim()
    const hasLastName = persona.last_name?.trim()
    const hasNickname = persona.nickname?.trim()
    const hasArchetype = persona.archetype?.trim()
    
    const fullName = hasFirstName ? `${persona.first_name}${hasLastName ? ` ${persona.last_name}` : ''}` : ''
    const displayName = hasNickname || fullName || hasArchetype || 'Unnamed'
    
    // Show archetype as a separate line if we have any name, otherwise it's already the display name
    const showArchetypeLine = hasArchetype && (hasFirstName || hasNickname)
    // Show original name if nickname is different from full name
    const showOriginalName = hasNickname && hasNickname !== fullName && fullName

    // Use profile picture if available, otherwise default
    const profilePicUrl = persona.profile_picture_url || '/static/images/default_avatar.jpg'

    // Check if text is truncated
    useEffect(() => {
        const checkTruncation = () => {
            if (bioRef.current && !isBioExpanded) {
                setIsBioTruncated(bioRef.current.scrollHeight > bioRef.current.clientHeight)
            }
            if (styleRef.current && !isStyleExpanded) {
                setIsStyleTruncated(styleRef.current.scrollHeight > styleRef.current.clientHeight)
            }
        }

        // Small delay to ensure DOM has updated
        const timeoutId = setTimeout(checkTruncation, 50)
        
        // Recheck on window resize
        window.addEventListener('resize', checkTruncation)
        return () => {
            clearTimeout(timeoutId)
            window.removeEventListener('resize', checkTruncation)
        }
    }, [persona.bio, persona.interaction_style, isEditing, isBioExpanded, isStyleExpanded])

    // Load candidate images when entering edit mode for admin
    useEffect(() => {
        if (isEditing && isAdmin && !isUserOwned) {
            loadCandidateImages()
        }
    }, [isEditing, isAdmin, isUserOwned])

    const loadCandidateImages = async () => {
        setIsLoadingCandidates(true)
        try {
            const result = await getPersonaCandidateImages(persona.id)
            setCandidateImages(result.candidate_images)
            // Initialize viewed images with the currently selected one
            const currentlySelected = result.candidate_images.find(img => img.is_selected)
            if (currentlySelected) {
                setViewedImageIds(new Set([currentlySelected.id]))
            }
        } catch (error) {
            console.error("Failed to load candidate images:", error)
        } finally {
            setIsLoadingCandidates(false)
        }
    }

    const handleEdit = () => {
        setIsEditing(true)
    }

    const handleCancel = () => {
        // Reset form fields
        setFirstName(persona.first_name)
        setLastName(persona.last_name || '')
        setBio(persona.bio)
        setInteractionStyle(persona.interaction_style)
        setPersonaNickname(persona.nickname || '')
        setError(null)
        setIsEditing(false)
        // Reset image rotation state
        setCandidateImages([])
        setViewedImageIds(new Set())
    }
    
    const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        
        setIsUploading(true)
        setError(null)
        
        try {
            await updatePersonaProfilePic(persona.id, file)
            // Notify parent component to refresh the persona data
            if (onPersonaUpdated) {
                // Create a new persona object with updated profile_picture_url
                // This is temporary until the real data is fetched
                const updatedPersona = {
                    ...persona,
                    profile_picture_url: URL.createObjectURL(file) // Create a temporary URL for immediate feedback
                }
                onPersonaUpdated(updatedPersona)
            }
        } catch (error) {
            console.error("Failed to upload photo:", error)
            setError("Failed to upload photo. Please try again.")
        } finally {
            setIsUploading(false)
        }
    }

    const getButtonText = () => {
        if (isFetchingImage) return 'Updating...'
        if (isLoadingCandidates) return 'Loading...'
        
        const unviewedCount = candidateImages.filter(img => !viewedImageIds.has(img.id)).length
        if (unviewedCount > 0) {
            return `Next (${unviewedCount} left)`
        }
        return 'Fetch new'
    }

    const getButtonTooltip = () => {
        const unviewedCount = candidateImages.filter(img => !viewedImageIds.has(img.id)).length
        if (unviewedCount > 0) {
            return `Rotate to next image (${unviewedCount} remaining)`
        }
        return 'Fetch new images from web'
    }

    const handleImageRotation = async () => {
        setIsFetchingImage(true)
        setError(null)
        setFetchMessage(null)
        
        try {
            // Find unviewed images
            const unviewedImages = candidateImages.filter(img => !viewedImageIds.has(img.id))
            
            if (unviewedImages.length > 0) {
                // Rotate to next unviewed image
                const nextImage = unviewedImages[0]
                await selectPersonaCandidateImage(persona.id, nextImage.id)
                
                // Update viewed set
                setViewedImageIds(prev => new Set([...prev, nextImage.id]))
                
                // Update the candidate images to reflect the new selection
                setCandidateImages(prev => prev.map(img => ({
                    ...img,
                    is_selected: img.id === nextImage.id
                })))
                
                setFetchMessage(`Switched to image ${viewedImageIds.size} of ${candidateImages.length}`)
                
                // Notify parent to refresh
                if (onPersonaUpdated) {
                    onPersonaUpdated(persona)
                }
            } else {
                // All images viewed, fetch new ones
                const result = await refetchPersonaImages(persona.id)
                
                if (result.images_stored > 0) {
                    const totalMsg = result.total_images ? ` (${result.total_images} total)` : ''
                    setFetchMessage(`Fetched ${result.images_stored} new image${result.images_stored > 1 ? 's' : ''}${totalMsg}`)
                    // Reload candidate images
                    await loadCandidateImages()
                    // Reset viewed images to empty so we can cycle through all of them
                    setViewedImageIds(new Set())
                    
                    if (onPersonaUpdated) {
                        onPersonaUpdated(persona)
                    }
                } else {
                    setFetchMessage(result.message || 'No new images found')
                }
            }
        } catch (error) {
            console.error("Failed to rotate/fetch images:", error)
            setError("Failed to update image. Please try again.")
        } finally {
            setIsFetchingImage(false)
        }
    }

    const handleSave = async () => {
        // Validate
        const hasFirstName = firstName.trim()
        const hasArchetype = persona.archetype?.trim()
        const canUseArchetype = isAdmin && persona.type !== PersonaType.CELEBRITY
        
        if (!bio.trim() || !interactionStyle.trim()) {
            setError('Bio and advising style are required')
            return
        }
        
        if (!hasFirstName && (!canUseArchetype || !hasArchetype)) {
            setError(canUseArchetype ? 'Either first name or archetype is required' : 'First name is required')
            return
        }

        setIsSaving(true)
        setError(null)

        try {
            const personaData: PersonaUpdate = {
                first_name: firstName.trim(),
                last_name: lastName.trim() || null,
                bio: bio.trim(),
                interaction_style: interactionStyle.trim(),
                nickname: personaNickname.trim() || null
            }

            const updatedPersona = await updatePersonaDetails(persona.id, personaData)

            // Update successful, exit edit mode
            setIsEditing(false)

            // Notify parent component
            if (onPersonaUpdated) {
                onPersonaUpdated(updatedPersona)
            }
        } catch (err) {
            setError('Failed to update persona details')
            console.error('Error updating persona:', err)
        } finally {
            setIsSaving(false)
        }
    }

    // View mode
    if (!isEditing) {
        return (
            <div className={`bg-white p-6 rounded-lg shadow border ${isInSquad ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}>
                <div className="flex items-start gap-4">
                    {/* Profile Picture */}
                    <div className="flex-shrink-0">
                        <img
                            src={profilePicUrl}
                            alt={`${persona.first_name} profile`}
                            className="w-16 h-16 rounded-full object-cover"
                        />
                    </div>

                    <div className="flex-grow">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-800">
                                    {displayName}
                                </h3>
                                {showOriginalName && (
                                    <p className="text-sm text-gray-500">
                                        {fullName}
                                    </p>
                                )}
                                {showArchetypeLine && (
                                    <p className="text-sm text-gray-600 italic">
                                        {persona.archetype}
                                    </p>
                                )}
                                <div className="flex gap-2 mt-1">
                                    {isInSquad && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            ✓ In Squad
                                        </span>
                                    )}
                                    {isInProject && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                                            ✓ In Project
                                        </span>
                                    )}
                                </div>
                            </div>

                            {(isUserOwned || isAdmin) && (
                                <button
                                    onClick={handleEdit}
                                    className="text-amber-800 hover:text-amber-900 text-sm font-medium"
                                >
                                    Edit
                                </button>
                            )}
                        </div>

                        {!isCompact && (
                            <div className="mb-4">
                                <p 
                                    ref={bioRef}
                                    className={`text-gray-600 ${isBioExpanded ? '' : 'line-clamp-4'}`}
                                >
                                    {persona.bio}
                                </p>
                                {isBioTruncated && (
                                    <button
                                        onClick={() => setIsBioExpanded(!isBioExpanded)}
                                        className="text-amber-800 hover:text-amber-900 text-sm font-medium mt-1"
                                    >
                                        {isBioExpanded ? 'See less' : 'See more'}
                                    </button>
                                )}
                            </div>
                        )}

                        {!isCompact && persona.interaction_style && (
                            <div className="text-sm text-gray-700 mb-4">
                                <strong className="block mb-1">Advising Style:</strong>
                                <p 
                                    ref={styleRef}
                                    className={`${isStyleExpanded ? '' : 'line-clamp-2'}`}
                                >
                                    {persona.interaction_style}
                                </p>
                                {isStyleTruncated && (
                                    <button
                                        onClick={() => setIsStyleExpanded(!isStyleExpanded)}
                                        className="text-amber-800 hover:text-amber-900 text-sm font-medium mt-1"
                                    >
                                        {isStyleExpanded ? 'See less' : 'See more'}
                                    </button>
                                )}
                            </div>
                        )}

                        {!isCompact && persona.expertise && persona.expertise.length > 0 && (
                            <div className="text-sm text-gray-700 mb-4">
                                <strong className="block mb-1">Expertise:</strong>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {persona.expertise.map((exp) => {
                                        const isProjectMatch = projectCategoryIds.includes(exp.category_id)
                                        return (
                                            <span
                                                key={exp.category_id}
                                                className={`inline-block px-2 py-1 rounded-full text-xs ${
                                                    isProjectMatch
                                                        ? 'bg-green-100 text-green-800 ring-2 ring-green-300'
                                                        : exp.expertise_level === 'primary'
                                                        ? 'bg-amber-100 text-amber-900'
                                                        : 'bg-gray-100 text-gray-700'
                                                }`}
                                            >
                                                {exp.category_name}
                                                {isProjectMatch && <span className="ml-1">✓</span>}
                                            </span>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {actionButtons && (
                            <div className="mt-4">
                                {actionButtons}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    // Edit mode
    return (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-start gap-4">
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                    <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                        <img
                            src={profilePicUrl}
                            alt={`${persona.first_name} profile`}
                            className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                        />
                        <div className="absolute bottom-0 right-0 bg-action p-1 rounded-full">
                            <Camera size={12} className="text-white" />
                        </div>
                        {isUploading && (
                            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs">Uploading...</span>
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                    />
                    {isAdmin && !isUserOwned && (
                        <button
                            onClick={handleImageRotation}
                            className="mt-2 flex items-center justify-center space-x-1 text-xs text-gray-600 hover:text-gray-800 disabled:text-gray-400"
                            disabled={isFetchingImage || isLoadingCandidates}
                            title={getButtonTooltip()}
                        >
                            <RefreshCw size={12} className={isFetchingImage ? 'animate-spin' : ''} />
                            <span>{getButtonText()}</span>
                        </button>
                    )}
                </div>

                <div className="flex-grow">
                    {error && (
                        <div className="mb-4 p-2 bg-rose-100 text-rose-700 rounded-md text-sm">
                            {error}
                        </div>
                    )}
                    {fetchMessage && (
                        <div className="mb-4 p-2 bg-green-100 text-green-700 rounded-md text-sm">
                            {fetchMessage}
                        </div>
                    )}

                    <div className="mb-4">
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                            First Name{isAdmin && persona.type && persona.type !== PersonaType.CELEBRITY && persona.archetype?.trim() ? '' : '*'}
                        </label>
                        <input
                            id="firstName"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md w-full"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name
                        </label>
                        <input
                            id="lastName"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md w-full"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
                            Nickname
                        </label>
                        <input
                            id="nickname"
                            type="text"
                            value={personaNickname}
                            onChange={(e) => setPersonaNickname(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md w-full"
                            placeholder="Optional nickname"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                            Bio*
                        </label>
                        <textarea
                            id="bio"
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            rows={3}
                            className="p-2 border border-gray-300 rounded-md w-full"
                        />
                    </div>

                    <div className="mb-4">
                        <label htmlFor="coachingStyle" className="block text-sm font-medium text-gray-700 mb-1">
                            Advising Style*
                        </label>
                        <textarea
                            id="coachingStyle"
                            value={interactionStyle}
                            onChange={(e) => setInteractionStyle(e.target.value)}
                            rows={3}
                            className="p-2 border border-gray-300 rounded-md w-full"
                        />
                    </div>

                    <div className="flex justify-between items-center mt-4">
                        <div className="flex space-x-3">
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                disabled={isSaving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-action text-plum rounded-md hover:bg-action-dark disabled:bg-amber-300"
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                        {/* Add action buttons in edit mode */}
                        {editActionButtons && (
                            <div>
                                {editActionButtons}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

            export default PersonaCard